/**
 * Shared navigation-config state. Both the sidebar (Layout) and the admin editor
 * read/write the same `config`, so edits to the currently-shown perspective
 * update the live sidebar immediately.
 *
 * Persistence goes through `navConfigStore` (localStorage in Phase 1, API in
 * Phase 3). The active `perspective` is a derived value: a stored/explicit
 * override if present, otherwise the default for the user's auth role — so it
 * auto-resolves once the role loads, with no effect/flicker.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserRole } from '../services/types';
import { NavConfig, Perspective } from '../nav/navTypes';
import { DEFAULT_NAV_CONFIG } from '../nav/defaultNavConfig';
import { defaultPerspectiveForRole } from '../nav/navSelectors';
import {
  loadNavConfig,
  saveNavConfig,
  loadStoredPerspective,
  saveStoredPerspective,
  reconcileWithCatalog,
  refreshNavConfigFromApi,
} from '../nav/navConfigStore';
import { useAuth } from './AuthContext';

interface NavConfigContextValue {
  config: NavConfig;
  setConfig: (next: NavConfig) => void;
  perspective: Perspective;
  setPerspective: (p: Perspective) => void;
  resetConfig: () => void;
}

const NavConfigContext = createContext<NavConfigContextValue | undefined>(undefined);

export function NavConfigProvider({ children }: { children: React.ReactNode }) {
  const { hasRole } = useAuth();
  const [config, setConfigState] = useState<NavConfig>(() => loadNavConfig());
  const [override, setOverride] = useState<Perspective | null>(() => loadStoredPerspective());

  // On mount, adopt the server-side config if an admin has saved one; until then
  // the localStorage/bundled-default config from the initial state stands. Server
  // unreachable / no doc / container missing → keeps the local config.
  useEffect(() => {
    let alive = true;
    refreshNavConfigFromApi().then((remote) => {
      if (alive && remote) setConfigState(remote);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Derive the auth role via hasRole (robust to tutor-shape differences).
  const role: UserRole = hasRole('admin') ? 'admin' : hasRole('operator') ? 'operator' : 'viewer';
  const perspective: Perspective = override ?? defaultPerspectiveForRole(role);

  const setConfig = useCallback((next: NavConfig) => {
    setConfigState(next);
    saveNavConfig(next);
  }, []);

  const setPerspective = useCallback((p: Perspective) => {
    setOverride(p);
    saveStoredPerspective(p);
  }, []);

  const resetConfig = useCallback(() => {
    const fresh = reconcileWithCatalog(DEFAULT_NAV_CONFIG);
    setConfigState(fresh);
    saveNavConfig(fresh);
  }, []);

  const value = useMemo(
    () => ({ config, setConfig, perspective, setPerspective, resetConfig }),
    [config, perspective, setConfig, setPerspective, resetConfig]
  );

  return <NavConfigContext.Provider value={value}>{children}</NavConfigContext.Provider>;
}

export function useNavConfig(): NavConfigContextValue {
  const ctx = useContext(NavConfigContext);
  if (!ctx) {
    throw new Error('useNavConfig must be used within NavConfigProvider');
  }
  return ctx;
}
